/* =====================================================================
 * 사용자 가이드 본문 레지스트리.
 *
 * 키는 `UserGuidePage` 목차 항목 키(`mode:<mode>` / `specialized:<id>` /
 * `admin-console`)와 같다. 본문은 항목마다 같은 폴더의 `.md` 파일로 두고
 * Vite `?raw` 로 문자열 그대로 읽어 온다 — 문서를 TS 문자열로 박으면
 * 백틱·표 이스케이프가 섞여 편집이 어렵고 컴포넌트 파일이 끝없이 길어진다.
 *
 * 캡처 이미지와 짧은 조작 영상(mp4)은 `images/<항목>/` 에 두고 본문에서 `images/...` 상대 경로로
 * 참조한다. 여기서 빌드 URL 로 바꿔 주므로 본문은 번들 경로를 몰라도 된다.
 *
 * 여기 없는 키는 페이지가 "준비중입니다" 로 떨어뜨린다.
 * ===================================================================== */

import agent from './agent.md?raw';
import docAgent from './doc-agent.md?raw';
import translation from './translation.md?raw';
import skillMarketplace from './skill-marketplace.md?raw';
import storage from './storage.md?raw';
import plugin from './plugin.md?raw';
import memory from './memory.md?raw';
import dashboard from './dashboard.md?raw';
import adminDashboard from './admin-dashboard.md?raw';
import adminConsole from './admin-console.md?raw';
import MEDIA_SIZES from './mediaSizes.json';

export interface UserGuideDoc {
  /** markdown 본문. 첫 `##` 앞까지가 도입 문단이다. */
  readonly body: string;
  /** 마지막으로 내용을 화면과 대조한 날짜 (YYYY-MM-DD). */
  readonly updatedAt: string;
}

export const USER_GUIDE_DOCS: Readonly<Record<string, UserGuideDoc>> = {
  'mode:agent': { body: agent, updatedAt: '2026-09-17' },
  'mode:doc-agent': { body: docAgent, updatedAt: '2026-09-17' },
  'mode:translation': { body: translation, updatedAt: '2026-09-17' },
  'mode:skill-marketplace': { body: skillMarketplace, updatedAt: '2026-09-17' },
  'mode:storage': { body: storage, updatedAt: '2026-09-17' },
  'mode:plugin': { body: plugin, updatedAt: '2026-09-17' },
  'mode:memory': { body: memory, updatedAt: '2026-09-17' },
  'mode:dashboard': { body: dashboard, updatedAt: '2026-09-17' },
  'mode:admin-dashboard': { body: adminDashboard, updatedAt: '2026-09-17' },
  'admin-console': { body: adminConsole, updatedAt: '2026-09-17' },
};

const GUIDE_IMAGES = import.meta.glob('./images/**/*.{png,webp,jpg,mp4}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/* 캡처·영상의 실제 픽셀 크기는 `mediaSizes.mjs` 로 생성한다(갱신을 잊으면 index.test 가 실패). */
/** 캡처가 로드되기 전에 자리를 잡아 둘 크기 [가로, 세로]. 모르는 경로면 undefined. */
export function guideMediaSize(src: string | undefined): readonly [number, number] | undefined {
  if (!src) return undefined;
  const size = (MEDIA_SIZES as Record<string, number[] | null>)[src.replace(/^\.\//, '')];
  return size && size.length === 2 ? [size[0], size[1]] : undefined;
}

/** 본문의 `images/agent/overview.png` 같은 상대 경로 → 번들 URL. */
export function resolveGuideImage(src: string | undefined): string | undefined {
  if (!src) return undefined;
  return GUIDE_IMAGES[`./${src.replace(/^\.\//, '')}`];
}

/** 저장소에 들어 있는 캡처 경로 목록 (`images/...`). 쓰이지 않는 캡처를 찾는 테스트용. */
export function listGuideImages(): string[] {
  return Object.keys(GUIDE_IMAGES).map((key) => key.replace(/^\.\//, ''));
}
